import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard, Users, CalendarClock, Images, Settings, LogOut, Heart,
  Loader2, BedDouble, Compass, DoorOpen, CalendarPlus, UserCog, UserRound, Car, MessageCircle,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const eventNavItems = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/admin/guests", label: "Guests", icon: Users, testid: "nav-guests" },
  { to: "/admin/messages", label: "Messages", icon: MessageCircle, testid: "nav-messages" },
  { to: "/admin/rooms", label: "Rooms", icon: BedDouble, testid: "nav-rooms" },
  { to: "/admin/travel", label: "Travel", icon: Car, testid: "nav-travel" },
  { to: "/admin/explore", label: "Explore", icon: Compass, testid: "nav-explore" },
  { to: "/admin/checkin", label: "Check-In", icon: DoorOpen, testid: "nav-checkin" },
  { to: "/admin/schedule", label: "Schedule", icon: CalendarClock, testid: "nav-schedule" },
  { to: "/admin/gallery", label: "Gallery", icon: Images, testid: "nav-gallery" },
  { to: "/admin/settings", label: "Event Settings", icon: Settings, testid: "nav-settings" },
];

const PLATFORM_PATHS = ["/admin/events", "/admin/admins", "/admin/account"];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(null);
  const [loading, setLoading] = useState(true);

  const isSuper = user?.role === "super_admin";
  const isPlatformRoute = PLATFORM_PATHS.some((p) => location.pathname.startsWith(p));

  const refreshEvents = async () => {
    const { data } = await api.get("/events");
    setEvents(data);
    setEventId((prev) => {
      if (prev && data.some((e) => e.id === prev)) return prev;
      return (data[0] && data[0].id) || null;
    });
    return data;
  };

  useEffect(() => {
    refreshEvents().finally(() => setLoading(false));
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const currentEvent = events.find((e) => e.id === eventId);
  const showSwitcher = events.length >= 2;

  const platformNav = [
    ...(isSuper
      ? [
          { to: "/admin/events", label: "Events", icon: CalendarPlus, testid: "nav-events" },
          { to: "/admin/admins", label: "Admins", icon: UserCog, testid: "nav-admins" },
        ]
      : []),
    { to: "/admin/account", label: "Account", icon: UserRound, testid: "nav-account" },
  ];

  const emptyMessage = isSuper
    ? "No events yet. Create one under Events."
    : "No events assigned to you yet. Ask a super admin for access.";

  return (
    <div className="flex min-h-screen bg-[#FAFAFA] text-[#0A0A0A]">
      <aside className="fixed left-0 top-0 z-20 flex h-screen w-60 flex-col border-r border-[#E5E5E5] bg-white">
        <div className="flex items-center gap-2 border-b border-[#E5E5E5] px-6 py-5">
          <Heart className="h-5 w-5 text-[#1A362D]" fill="#1A362D" />
          <span className="font-serif text-xl font-semibold">Events</span>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {eventNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-[#1A362D] text-white" : "text-[#525252] hover:bg-[#F4EFE6]"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
          <div className="my-3 border-t border-[#E5E5E5]" />
          {platformNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-[#1A362D] text-white" : "text-[#525252] hover:bg-[#F4EFE6]"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-[#E5E5E5] p-3">
          <div className="mb-2 px-3 text-xs text-[#525252]">{user?.email}</div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-[#525252] transition-colors hover:bg-[#F4EFE6]"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>
        </div>
      </aside>

      <main className="ml-60 flex-1">
        <div className="flex items-center justify-between border-b border-[#E5E5E5] bg-white px-8 py-4">
          <div className="flex items-center gap-3">
            {events.length === 1 && (
              <span className="text-sm text-[#525252]">
                Managing <span className="font-medium text-[#0A0A0A]">{events[0].name}</span>
              </span>
            )}
            {showSwitcher && (
              <>
                <span className="text-sm text-[#525252]">Managing event</span>
                <Select value={eventId || ""} onValueChange={setEventId}>
                  <SelectTrigger className="w-[240px]" data-testid="event-selector">
                    <SelectValue placeholder="Select event" />
                  </SelectTrigger>
                  <SelectContent>
                    {events.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}
            {events.length === 0 && (
              <span className="text-sm text-[#525252]">No event selected</span>
            )}
          </div>
        </div>

        <div className="p-8">
          {loading ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-[#1A362D]" />
            </div>
          ) : isPlatformRoute ? (
            <Outlet context={{ eventId, currentEvent, events, refreshEvents, setEventId }} />
          ) : !eventId ? (
            <div className="rounded-lg border border-[#E5E5E5] bg-white p-10 text-center text-[#525252]">
              {emptyMessage}
            </div>
          ) : (
            <Outlet context={{ eventId, currentEvent, events, refreshEvents, setEventId }} />
          )}
        </div>
      </main>
    </div>
  );
}
